import { useNavigate } from 'react-router-dom';
import {
  Bin,
  Chip,
  DuplicateIcon,
  EditIcon,
  InfoCard,
  InfoCardDetail,
  IOption,
  LinkButton,
  TextIcon,
  Typography,
  ViewIcon,
} from '@weareopenr/component-library';
import { useAuthWrapper } from '@weareopenr/authkit';
import { ITimetable } from 'src/api/getTimetables';
import { FEATURE_FLAGS } from 'src/constants';
import { useDeleteTimetablePrompt } from 'src/hooks/useDeleteTimetablePrompt';
import { usePermissions } from 'src/hooks/usePermissions/usePermissions';
import { DeleteEntityPrompts } from 'src/components/DeleteEntityPrompts';
import { SpinnerIcon } from 'src/components/SpinnerIcon';
import { Routes } from 'src/routes';

export interface TimetableCardProps {
  timetable: ITimetable;
  rangesEnabled: boolean;
  isTrayOpen?: boolean;
  onDuplicate: (timetable: ITimetable) => void;
  onEditDetails: (timetable: ITimetable) => void;
  onRename: (timetable: ITimetable) => void;
}

export const TimetableCard = ({
  timetable,
  rangesEnabled,
  isTrayOpen = false,
  onDuplicate,
  onEditDetails,
  onRename,
}: TimetableCardProps) => {
  const navigate = useNavigate();
  const { canCreateTimetables, canUpdateTimetables } = usePermissions();
  const { featureFlags } = useAuthWrapper();
  const isMenuSetsEnabled = featureFlags.includes(FEATURE_FLAGS.MENU_SETS);

  const timetablePath = `${Routes.TIMETABLES}/${timetable.timetable_id}`;

  const deletePrompt = useDeleteTimetablePrompt({
    timetableId: timetable.timetable_id,
    timetableName: timetable.name,
    genericErrorMessage: 'We have been unable to complete your request. Please try again',
  });

  const details: InfoCardDetail[] = [
    // Each menu set counts once, alongside the menus.
    isMenuSetsEnabled
      ? {
          text: 'Menus and Menu Sets used',
          detail: String(timetable.menus.length + (timetable.menu_sets?.length ?? 0)),
        }
      : { text: 'Menus used', detail: String(timetable.menus.length) },
    { text: 'Price Schemes used', detail: String(timetable.price_schemes.length) },
  ];

  const ellipsisOptions: IOption[] = [
    {
      id: 'view',
      name: 'View',
      icon: ViewIcon,
      onOptionClick: () => navigate(timetablePath),
    },
    {
      id: 'edit-name',
      name: 'Edit name',
      icon: TextIcon,
      isDisabled: !canUpdateTimetables,
      onOptionClick: () => onRename(timetable),
    },
    {
      id: 'edit-details',
      name: 'Edit details',
      icon: EditIcon,
      isDisabled: !canUpdateTimetables,
      onOptionClick: () => onEditDetails(timetable),
    },
    {
      id: 'duplicate',
      name: 'Duplicate',
      icon: DuplicateIcon,
      isDisabled: !canCreateTimetables,
      onOptionClick: () => onDuplicate(timetable),
    },
    {
      id: 'delete',
      name: 'Delete',
      icon: deletePrompt.isCheckingCanDelete ? SpinnerIcon : Bin,
      hoverColour: 'tomato',
      isDisabled: deletePrompt.isCheckingCanDelete,
      persistMenuOnClick: true,
      onOptionClick: deletePrompt.openDeletePrompt,
      dataTestId: 'open-remove-prompt',
    },
  ];

  const getFootnote = () => {
    if (!rangesEnabled) return null;
    if (timetable.ranges.length === 0) return <Typography size="xsmall">No Ranges</Typography>;

    return timetable.ranges.map((range) => (
      <Chip key={range.range_id} label={range.name} icon="priceBand" backgroundColour="grape" />
    ));
  };

  return (
    <div>
      <InfoCard
        label="Menu & pricing timetable"
        labelColour="blueberry"
        title={timetable.name}
        details={details}
        footnote={getFootnote()}
        // Hide the card's actions while a tray is open so they can't conflict with the tray's form.
        ellipsisOptions={isTrayOpen ? undefined : ellipsisOptions}
        action={
          isTrayOpen ? undefined : <LinkButton onClick={() => navigate(timetablePath)}>View Timetable</LinkButton>
        }
      />
      <DeleteEntityPrompts
        entityLabel="timetable"
        entityName={timetable.name}
        unaffectedItems="menu or price scheme"
        deletePrompt={deletePrompt}
      />
    </div>
  );
};

export default TimetableCard;
